import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import download from 'downloadjs';
import { useForm } from '@tanstack/react-form';
import {
  ButtonType,
  GenerateSampleCodesDto,
  ProductDto,
} from '@src/types/types';
import { Button } from '@components/atoms/Button';
import Input from '@components/atoms/Input';
import { Dialog } from '@components/atoms/Dialog';
import { apiClient } from '@src/api/client';

type GenerateCodesFormValues = {
  numberOfCodes: number | null;
  generatedFor: string;
};

export function GenerateSampleCodesView() {
  const [selectedProduct, setSelectedProduct] = useState<ProductDto | null>(
    null,
  );

  const { t } = useTranslation('view', {
    keyPrefix: 'internal.generateSampleCodes',
  });
  const { t: tError } = useTranslation('view', { keyPrefix: 'error' });

  const { data, isLoading } = apiClient.useQuery(
    'get',
    '/api/internal/products',
  );

  const { mutateAsync, error } = apiClient.useMutation(
    'post',
    '/api/internal/generateSampleCodes',
    {
      onSuccess: (data, options) => {
        const body = options.body as GenerateSampleCodesDto;
        //File name of the generated file should follow this pattern: 100_codes_productid_generatedfor_date
        download(
          data as Blob,
          `${body.numberOfCodes}_codes_${body.productID}_${body.generatedFor}_${new Date().toISOString()}.csv`,
          'text/csv',
        );
        setSelectedProduct(null);
        form.reset();
      },
    },
  );

  const defaultValues: GenerateCodesFormValues = {
    numberOfCodes: null,
    generatedFor: '',
  };

  const form = useForm({
    defaultValues,
    validators: {
      onSubmitAsync: async ({ value }) => {
        if (!selectedProduct) {
          return {
            fields: {
              numberOfCodes: t('selectProduct'),
            },
          };
        }

        if (!value.numberOfCodes || value.numberOfCodes < 1) {
          return {
            fields: {
              numberOfCodes: t('minimumCodes'),
            },
          };
        }

        if (!value.generatedFor || value.generatedFor.length < 5) {
          return {
            fields: {
              generatedFor: t('generatedForRequired'),
            },
          };
        }

        try {
          await mutateAsync({
            body: {
              productID: `${selectedProduct.identifier}`,
              numberOfCodes: value.numberOfCodes,
              generatedFor: value.generatedFor,
            },
            parseAs: 'blob',
          });
        } catch (error) {
          console.error(t('error'), error);
          return {
            fields: {
              numberOfCodes: tError('connectionError'),
            },
          };
        }

        return null;
      },
    },
  });

  // Sort by product id
  const sortedData = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.sort((a, b) => a.identifier.localeCompare(b.identifier));
  }, [data]);

  if (isLoading || !data) {
    return <div>Loading...</div>;
  }

  return (
    <div className="grow p-10">
      <h1 className="text-4xl mb-4">{t('selectProduct')}</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {sortedData.map((product) => {
          return (
            <button
              className="p-4 rounded-xl bg-ivarioBlue text-white hover:bg-ivarioBlue-hover"
              key={product.id}
              onClick={() => setSelectedProduct(product)}
            >
              <h2 className="text-2xl mb-1">{product.title}</h2>
              <p>Product ID: {product.identifier}</p>
            </button>
          );
        })}
      </div>
      {selectedProduct && (
        <Dialog
          onClose={() => {
            form.reset();
            setSelectedProduct(null);
          }}
        >
          <h2 className="text-2xl">{selectedProduct.title}</h2>
          <p className="mb-4">Product ID: {selectedProduct.identifier}</p>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <form.Field
              name="numberOfCodes"
              validators={{
                onChange: ({ value }) => {
                  if (!value) return t('minimumCodes');
                  if (value < 1) return t('minimumCodes');
                  return undefined;
                },
              }}
            >
              {(field) => (
                <Input
                  label={t('numberOfCodes')}
                  value={field.state.value ?? ''}
                  handleChange={(e) => {
                    const value =
                      e.target.value === ''
                        ? null
                        : Number.parseInt(e.target.value);
                    field.handleChange(value);
                  }}
                  type="number"
                  name={field.name}
                  id={field.name}
                  testId="number-of-codes"
                  min={1}
                  max={1000}
                  containerClassName="mb-4"
                  errors={field.state.meta.errors}
                  touched={field.state.meta.isTouched}
                  handleBlur={field.handleBlur}
                />
              )}
            </form.Field>

            <form.Field
              name="generatedFor"
              validators={{
                onChange: ({ value }) => {
                  if (!value) return t('generatedForRequired');
                  if (value.length < 5) return t('generatedForRequired');
                  return undefined;
                },
              }}
            >
              {(field) => (
                <Input
                  label={t('generatedFor')}
                  value={field.state.value ?? ''}
                  handleChange={(e) => field.handleChange(e.target.value)}
                  type="text"
                  name={field.name}
                  id={field.name}
                  testId="generated-for"
                  placeholder={t('generatedForPlaceholder')}
                  containerClassName="mb-4"
                  errors={field.state.meta.errors}
                  touched={field.state.meta.isTouched}
                  handleBlur={field.handleBlur}
                />
              )}
            </form.Field>

            {error && (
              <p className="text-error mb-4">{(error as Error).message}</p>
            )}

            <div className="flex flex-row space-x-2 md:space-x-10">
              <form.Subscribe
                selector={(state) => [state.canSubmit, state.isSubmitting]}
              >
                {([canSubmit, isSubmitting]) => (
                  <Button
                    label={t('generate')}
                    buttonType={ButtonType.PRIMARY}
                    type="submit"
                    disabled={!canSubmit}
                    loading={isSubmitting}
                    data-testid="generate"
                  />
                )}
              </form.Subscribe>

              <Button
                label={t('close')}
                buttonType={ButtonType.SECONDARY}
                onClick={() => setSelectedProduct(null)}
                data-testid="close"
              />
            </div>
          </form>
        </Dialog>
      )}
    </div>
  );
}
